import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useSession } from '../session';

interface Preview {
  valid: true;
  /** Set when the visitor is already signed in: the new workspace will be theirs. */
  signedInAs: { email: string; displayName: string } | null;
}

/**
 * The way in during closed testing.
 *
 * Everything here hangs on the code. It is checked before the form is drawn, because a code that has
 * already been spent is worth saying at the door rather than after somebody has chosen a password, and
 * checked again on the server when the form is sent, because the first answer is only ever a preview.
 */
export function SignUp() {
  const { code: codeFromUrl } = useParams();
  const { refresh, productName } = useSession();
  const navigate = useNavigate();
  const [code, setCode] = useState(codeFromUrl ?? '');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [checking, setChecking] = useState(codeFromUrl !== undefined);
  const [workspaceName, setWorkspaceName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (codeFromUrl === undefined) return;
    setChecking(true);
    api<Preview>(`/api/signup/${encodeURIComponent(codeFromUrl)}`)
      .then(setPreview)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That invite code could not be checked'))
      .finally(() => setChecking(false));
  }, [codeFromUrl]);

  async function checkCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setChecking(true);
    try {
      setPreview(await api<Preview>(`/api/signup/${encodeURIComponent(code)}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That invite code could not be checked');
    } finally {
      setChecking(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ next: 'mfa' | 'enrol'; tenantId: string }>('/api/signup', {
        method: 'POST',
        body: preview?.signedInAs
          ? { code, workspaceName }
          : { code, workspaceName, displayName, email, password },
      });
      await refresh();
      navigate(result.next === 'enrol' ? '/enrol' : `/workspaces/${result.tenantId}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The workspace could not be created');
      // A code spent between the preview and the form is gone; send them back to the first step rather
      // than leaving a dead form on screen.
      if (err instanceof ApiError && (err.status === 404 || err.status === 410)) setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  if (!preview) {
    return (
      <div className="card narrow">
        <h1>Create a workspace</h1>
        <p className="lede">
          {productName} is in closed testing, so a workspace starts from an invite code. If you have one, enter
          it here.
        </p>
        {error && <div className="alert">{error}</div>}
        <form onSubmit={checkCode}>
          <label>
            Invite code
            <input
              id="signup-code"
              required
              maxLength={64}
              autoComplete="off"
              spellCheck={false}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              className="mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <span className="small muted">Case and hyphens do not matter.</span>
          </label>
          <button type="submit" className="block" disabled={checking || code.trim() === ''}>{checking ? 'Checking…' : 'Continue'}</button>
        </form>
        <p className="small muted" style={{ marginTop: 20, marginBottom: 0 }}>
          Been invited to a workspace that already exists? Use the link in that invitation instead, or{' '}
          <Link to="/login">sign in</Link>.
        </p>
      </div>
    );
  }

  return (
    <div className="card narrow">
      <h1>Create a workspace</h1>
      <p className="lede">
        A workspace is your company in {productName}: its people, and the Salesforce orgs it connects to. You
        will be its owner.
      </p>
      {error && <div className="alert">{error}</div>}
      <form onSubmit={submit}>
        <label>
          Workspace name
          <input
            id="signup-workspace"
            required
            maxLength={120}
            placeholder="Your company"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
          />
          <span className="small muted">Usually your company's name. You can change it later.</span>
        </label>

        {preview.signedInAs ? (
          <p className="small muted">
            Signed in as {preview.signedInAs.displayName || preview.signedInAs.email}. The workspace will be
            created under this account.
          </p>
        ) : (
          <>
            <label>
              Your name
              <input id="signup-name" required maxLength={120} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </label>
            <label>
              Work email
              <input
                id="signup-email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              Choose a password
              <input
                id="signup-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span className="small muted">
                At least 12 characters. It is checked against known breaches. You will set up an authenticator
                app and a passkey next.
              </span>
            </label>
          </>
        )}

        {/* Stacked, both full width: the way back is a real button, but it sits under the one that matters. */}
        <div className="stack" style={{ gap: 10 }}>
          <button type="submit" className="block" disabled={busy}>{busy ? 'Creating…' : 'Create workspace'}</button>
          <button type="button" className="secondary block" onClick={() => setPreview(null)} disabled={busy}>Use a different code</button>
        </div>
      </form>
    </div>
  );
}
